import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, QrCode, Copy, CheckCircle2 } from 'lucide-react';
import { getWalletConnectQRUri, onWalletConnectDisplayUri } from '../lib/walletConnect';
import { toast } from 'sonner';

interface WalletConnectQRModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function WalletConnectQRModal({ open, onOpenChange }: WalletConnectQRModalProps) {
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setQrUri(null);
      setCopied(false);
      return;
    }

    // Get existing URI or listen for new one
    const existingUri = getWalletConnectQRUri();
    if (existingUri) {
      setQrUri(existingUri);
    }

    // Listen for URI updates
    const unsubscribe = onWalletConnectDisplayUri((uri: string) => {
      setQrUri(uri);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [open]);

  const handleCopy = async () => {
    if (qrUri) {
      try {
        await navigator.clipboard.writeText(qrUri);
        setCopied(true);
        toast.success('Copied to clipboard', {
          description: 'WalletConnect URI copied. You can paste it in your mobile wallet.',
          duration: 3000,
        });
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        toast.error('Failed to copy', {
          description: 'Please manually copy the URI.',
          duration: 3000,
        });
      }
    }
  };

  // Generate QR code using a simple library or service
  // For now, we'll use a QR code API service
  const qrCodeUrl = qrUri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrUri)}`
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            Scan QR Code
          </DialogTitle>
          <DialogDescription>
            Scan this QR code with your mobile wallet app to connect
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!qrUri ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-400 mb-4" />
              <p className="text-sm text-gray-400">Generating connection QR code...</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center space-y-4">
                <div className="bg-white p-4 rounded-lg">
                  {qrCodeUrl && (
                    <img
                      src={qrCodeUrl}
                      alt="WalletConnect QR Code"
                      className="w-64 h-64"
                    />
                  )}
                </div>

                <div className="w-full space-y-2">
                  <Button
                    onClick={handleCopy}
                    variant="outline"
                    className="w-full"
                    disabled={!qrUri || copied}
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2 h-4 w-4" />
                        Copy Connection URI
                      </>
                    )}
                  </Button>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-gray-400 text-center">
                    <strong className="text-yellow-400">⚠️ iOS Users:</strong> If MetaMask opens automatically, CANCEL it.
                  </p>
                  <p className="text-xs text-gray-400 text-center">
                    Then manually open <strong className="text-blue-400">Trust Wallet</strong> app → Settings → WalletConnect → New Connection → Scan QR code.
                  </p>
                  <p className="text-xs text-gray-400 text-center">
                    Trust Wallet is required for admin access and supports more blockchains.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

